import { useCurrentUser, useIsArticAdmin } from '@artic/api-client';
import { resolvePickerLocale, selectableLocales } from '@artic/i18n';
import { ChangePartnerMenu } from '@artic/ui/change-partner-menu';
import {
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@artic/ui/dropdown-menu';
import { EnvironmentIndicator } from '@artic/ui/environment-indicator';
import { ArticTour } from '@artic/ui/joyride';
import {
  LAST_PARTNER_COOKIE,
  beginAuthLogout,
  readCookie,
  setLastPartnerCookie,
  setPartnerCookie,
} from '@artic/ui/lib/auth-cookies';
import { getEnvironment } from '@artic/ui/lib/environment';
import { NavUser as NavUserShell } from '@artic/ui/nav-user';
import {
  LogoutIcon,
  LanguageCircleIcon,
  Moon02Icon,
  Route01Icon,
  Settings05Icon,
  Sun02Icon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useRouterState } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { redirectToHub } from '../auth';
import { partnerSwitchDestination } from './partner-switch-destination';
import { usePartnerTour } from './use-partner-tour';
import { useTheme } from './use-theme';

/** The partner_key of the "Artic" partner, always pinned first in the switcher. */
const ARTIC_PARTNER_KEY = 'ART';

const COOKIE_OPTIONS = (() => {
  const domain = import.meta.env['VITE_AUTH_COOKIE_DOMAIN'] as string | undefined;
  return domain ? { domain } : {};
})();

export function NavUser() {
  const { i18n, t } = useTranslation('common');
  const { user, partners, activePartner } = useCurrentUser();
  const isAdmin = useIsArticAdmin();
  const tour = usePartnerTour(isAdmin);
  const { theme, setTheme } = useTheme();
  const matches = useRouterState({ select: (state) => state.matches });

  const displayName =
    (user.profile_name ??
      [user.profile_first_name, user.profile_last_name].filter(Boolean).join(' ')) ||
    user.email;

  const isDark = theme === 'dark';

  // Users choose between British and American English. Generic `en` stays the
  // canonical bundle and the detection fallback, but it is only offered as a
  // picker option on local development.
  const localeOptions = selectableLocales({ includeGeneric: getEnvironment() === 'local' });
  const currentLocale = resolvePickerLocale(i18n.resolvedLanguage ?? i18n.language, localeOptions);

  // Remember the active partner as the "last non-Artic partner viewed" so the
  // switcher can pin it after the admin jumps to Artic and wants to jump back.
  useEffect(() => {
    const active = partners.find((p) => p.partner_key === activePartner);
    if (active && active.partner_key !== ARTIC_PARTNER_KEY) {
      setLastPartnerCookie(active.partner_key, COOKIE_OPTIONS);
    }
  }, [activePartner, partners]);

  const handleSelectPartner = (partnerKey: string) => {
    setPartnerCookie(partnerKey, COOKIE_OPTIONS);
    if (partnerKey !== ARTIC_PARTNER_KEY) {
      setLastPartnerCookie(partnerKey, COOKIE_OPTIONS);
    }
    // A full reload re-reads the cookie so nav, feature flags, and every
    // partner-scoped query reflect the new partner. Where it reloads TO depends
    // on whether the current URL names a record owned by the old partner — see
    // partnerSwitchDestination.
    switch (partnerSwitchDestination(matches[matches.length - 1])) {
      case 'home':
        window.location.href = '/';
        break;
      case 'stripSearch':
        window.location.href = window.location.pathname;
        break;
      default:
        window.location.reload();
    }
  };

  return (
    <>
      <NavUserShell user={{ name: displayName, email: user.email }} {...tour.navControl}>
        {isAdmin ? (
          <>
            <EnvironmentIndicator />
            <ChangePartnerMenu
              partners={partners.map((p) => ({
                key: p.partner_key,
                name: p.partner_display_name?.trim() || p.partner_name,
              }))}
              activePartnerKey={activePartner}
              articPartnerKey={ARTIC_PARTNER_KEY}
              lastPartnerKey={readCookie(LAST_PARTNER_COOKIE)}
              onSelect={handleSelectPartner}
              {...tour.changePartnerControl}
            />
            <DropdownMenuItem onClick={() => tour.startTour()}>
              <HugeiconsIcon icon={Route01Icon} strokeWidth={2} />
              {t('account.takeTheTour')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        ) : null}
        <DropdownMenuGroup>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <HugeiconsIcon icon={LanguageCircleIcon} strokeWidth={2} />
              <span className="flex flex-1 items-center justify-between gap-2">
                <span>{t('locale.label')}</span>
                <span className="text-muted-foreground text-xs">
                  {t(`locale.${currentLocale}`)}
                </span>
              </span>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <DropdownMenuRadioGroup
                aria-label={t('locale.label')}
                value={currentLocale}
                onValueChange={(locale) => {
                  void i18n.changeLanguage(locale);
                }}
              >
                {localeOptions.map((locale) => (
                  <DropdownMenuRadioItem key={locale} value={locale}>
                    {t(`locale.${locale}`)}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <DropdownMenuItem
            onClick={(e) => {
              e.preventDefault();
              setTheme(isDark ? 'light' : 'dark');
            }}
          >
            <HugeiconsIcon icon={isDark ? Sun02Icon : Moon02Icon} strokeWidth={2} />
            {isDark ? t('account.lightMode') : t('account.darkMode')}
          </DropdownMenuItem>
          <DropdownMenuItem>
            <HugeiconsIcon icon={Settings05Icon} strokeWidth={2} />
            {t('terms.settings')}
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => {
            beginAuthLogout(COOKIE_OPTIONS);
            redirectToHub();
          }}
        >
          <HugeiconsIcon icon={LogoutIcon} strokeWidth={2} />
          {t('account.logOut')}
        </DropdownMenuItem>
      </NavUserShell>
      {isAdmin ? <ArticTour steps={tour.steps} run={tour.run} onEvent={tour.onEvent} /> : null}
    </>
  );
}
